/**
 * DI-3-S11-FE — a session's folder found under the field-data ROOT
 * (`handleStore.ts:resolveUnderRoot`), for a record this browser holds.
 *
 * The root resolves a FOLDER for a record that already names it
 * (`SessionRecord.folderName`); it never creates, picks or re-targets a
 * session (lock never-make-the-wrong-session-active). A drop is matched by
 * its folder's NAME, so when another live record of this browser has the
 * same folder name the name alone cannot tell the two apart.
 *
 * Which folder it really is stays the reader's check (the sets' stable ids /
 * hashes, DI-3-S4-FE / DI-3-S15-FE), as for any folder.
 *
 * DI-3-S16-FE (smoke R2-F4 / R2-F5) — ONE order of the sources that need no
 * gesture, for every reader (the Resume line, the lane's `canDrive`, the
 * click's `laneFolder`, the drop session's `resume.ts`):
 *
 *   handle      the record's own folder handle, permission "granted";
 *   set-folder  a folder picked on the card for one of the record's sets
 *               (`handle:set:<daId>`, DI-3-P13), permission "granted" — the
 *               pick already counted the set's file names in it;
 *   root        the record's folder under the granted root: the path
 *               recorded at drop time, else the search by name up to three
 *               levels down (`resolveUnderRoot`); a record dropped as loose
 *               files is never looked for there (its name is a placeholder);
 *   shared      the root has the name, but cannot tell which folder (two
 *               live records of the name, or several folders of the name
 *               and the file names did not pick one) → the picker, and why;
 *   ask         none of them → the picker / the prompt (a gesture).
 *
 * A source only says WHERE to read, never whether it is the right folder.
 *
 * DI-3-S18-FE — a folder RENAMED on disk: the root finds it by the record's
 * files (`resolveUnderRoot`'s content fallback, proved by every `name|size`
 * key of THIS record) and `rootFolderFor` heals the record — its folder's
 * current name and path (`renameSessionFolder`), so the next lookup is the
 * free one. A folder picked for a paused session heals it the same way
 * (`healFromFolder`).
 */
import type { CollectResult, DirectoryHandleLike } from "./dropToFiles.js";
import {
  recall,
  resolveUnderRoot,
  rootPathOf,
  type RootLookup,
} from "./handleStore.js";
import { setFolderKey } from "./setFolderKey.js";
import { normalizedHashKey, rowHashKey } from "../parse/hashKey.js";
import {
  isLooseDrop,
  listSessions,
  renameSessionFolder,
  setRootPath,
  type SessionRecord,
} from "../session/sessionStore.js";

export type RootFolder =
  | {
      kind: "resolved";
      handle: DirectoryHandleLike;
      rootName: string;
      folderName: string;
      /** DI-3-S16-FE — the names from the root down to the folder (it included). */
      path: string[];
    }
  /**
   * The root holds the name, but cannot tell which folder is this record's:
   * another live record of this browser has the name too, or (DI-3-S16-FE)
   * `count` folders of the name and the file names did not pick one.
   */
  | {
      kind: "shared";
      rootName: string;
      folderName: string;
      count?: number | undefined;
    }
  /** No folder: `rootName` when a granted root was asked and has none. */
  | { kind: "none"; rootName?: string | undefined };

export interface RootFolderDeps {
  sessions?: () => Promise<SessionRecord[]>;
  resolve?: typeof resolveUnderRoot;
  /** DI-3-S16-FE — files the path found by name on the record. */
  saveRootPath?: (sessionId: string, path: readonly string[]) => Promise<void>;
  /** DI-3-S18-FE — heals the record after a find by its files. */
  renameFolder?: typeof renameSessionFolder;
}

/**
 * `<root>\<folder>` — the words the card and the banner show; DI-3-S16-FE
 * `<root>\<a>\<b>\<folder>` for a folder deeper down.
 */
export function rootPath(
  rootName: string,
  folder: string | readonly string[],
): string {
  return [rootName, ...(typeof folder === "string" ? [folder] : folder)].join(
    "\\",
  );
}

/** DI-3-S16-FE — the record's files as `name|size` keys (every set's `rowHashes`). */
export function recordFileKeys(
  rec: Pick<SessionRecord, "clusters">,
): Set<string> {
  const out = new Set<string>();
  for (const c of rec.clusters ?? [])
    for (const k of Object.keys(c.rowHashes ?? {}))
      out.add(normalizedHashKey(k));
  return out;
}

/**
 * The record's folder under the root, never prompting. "none" when the
 * record is not in this browser (a server-only view: its folder name is a
 * placeholder), was dropped as loose files (DI-3-S16-FE), when there is no
 * granted root, or when the root has no folder of that name.
 */
export async function rootFolderFor(
  rec: Pick<SessionRecord, "sessionId">,
  deps: RootFolderDeps = {},
): Promise<RootFolder> {
  try {
    const all = await (deps.sessions ?? listSessions)();
    const own = all.find((s) => s.sessionId === rec.sessionId);
    if (!own?.folderName || isLooseDrop(own)) return { kind: "none" };
    // DI-3-S11-FE — another live record of the name: the name alone cannot
    // tell them apart; DI-3-S16-FE — the file names can (`verify`)
    const shared = all.some(
      (s) =>
        s.sessionId !== own.sessionId &&
        s.folderName === own.folderName &&
        s.endedAt === undefined,
    );
    const found: RootLookup<DirectoryHandleLike> | null = await (
      deps.resolve ?? resolveUnderRoot
    )<DirectoryHandleLike>(
      own.folderName,
      {},
      {
        rootPath: own.rootPath,
        keys: recordFileKeys(own),
        verify: shared,
        cacheKey: own.sessionId,
      },
    );
    if (!found) return { kind: "none" };
    if (found.kind === "absent")
      return { kind: "none", rootName: found.rootName };
    if (found.kind === "ambiguous")
      return {
        kind: "shared",
        rootName: found.rootName,
        folderName: own.folderName,
        ...(found.count > 1 ? { count: found.count } : {}),
      };
    if (typeof found.handle.values !== "function") return { kind: "none" };
    // DI-3-S18-FE — found by its files under ANOTHER name (renamed on disk;
    // `resolveUnderRoot` proved it by THIS record's own keys): THIS record
    // heals — its folder's current name and path — so the next lookup is
    // the free one (the recorded path)
    const name = found.name || own.folderName;
    if (found.byContent === true && name !== own.folderName) {
      await (deps.renameFolder ?? renameSessionFolder)(
        own.sessionId,
        name,
        found.path,
      ).catch(() => false);
    } else if (
      // a root remembered after the drop: the first success files the path
      found.path.length > 0 &&
      found.path.join("\n") !== (own.rootPath ?? []).join("\n")
    )
      await (deps.saveRootPath ?? setRootPath)(own.sessionId, found.path).catch(
        () => undefined,
      );
    return {
      kind: "resolved",
      handle: found.handle,
      rootName: found.rootName,
      folderName: name,
      path: found.path,
    };
  } catch {
    return { kind: "none" };
  }
}

/**
 * DI-3-S16-FE — a folder picked on the card for one of the record's sets
 * (DI-3-P13, `handle:set:<daId>`) whose permission is "granted": readable
 * without a gesture. The first such set in the record's order.
 */
export async function setFolderOf(
  rec: Pick<SessionRecord, "clusters">,
  deps: { recall?: typeof recall } = {},
): Promise<{
  handle: DirectoryHandleLike;
  key: string;
  daId: string;
  folderName: string;
} | null> {
  for (const c of rec.clusters ?? []) {
    const daId = c.dataAcquisitionId;
    if (!daId) continue;
    const key = setFolderKey(daId);
    const got = await (deps.recall ?? recall)<DirectoryHandleLike>(key).catch(
      () => null,
    );
    if (got?.permission !== "granted") continue;
    if (typeof got.handle.values !== "function") continue;
    return { handle: got.handle, key, daId, folderName: got.handle.name };
  }
  return null;
}

/**
 * Where the card's "Resume the upload" will take the folder from, read
 * without a gesture (the line's words; DI-3-S16-FE — the automatic start
 * runs on "handle", "set-folder" and "root").
 */
export type ResumeSource =
  | { kind: "handle" }
  | { kind: "set-folder"; folderName: string }
  | { kind: "root"; path: string }
  | {
      kind: "shared";
      folderName: string;
      rootName?: string | undefined;
      /** Several folders of the name under the root (DI-3-S16-FE). */
      count?: number | undefined;
    }
  /** `loose` — the record was dropped as files (DI-3-S16-FE). */
  | { kind: "ask"; loose?: boolean | undefined };

/** Does the source need no gesture (the lane may start by itself)? */
export function gestureFree(source: ResumeSource | null): boolean {
  return (
    source?.kind === "handle" ||
    source?.kind === "set-folder" ||
    source?.kind === "root"
  );
}

export async function resumeSourceOf(
  rec: Pick<SessionRecord, "sessionId" | "handleKey"> &
    Partial<Pick<SessionRecord, "clusters">>,
  deps: RootFolderDeps & { recall?: typeof recall } = {},
): Promise<ResumeSource> {
  try {
    // 1. the record's own handle
    if (rec.handleKey) {
      const own = await (deps.recall ?? recall)<unknown>(rec.handleKey).catch(
        () => null,
      );
      if (own?.permission === "granted") return { kind: "handle" };
    }
    const all = await (deps.sessions ?? listSessions)();
    const stored = all.find((s) => s.sessionId === rec.sessionId);
    // 2. a folder picked on the card for one of its sets
    const clusters = rec.clusters ?? stored?.clusters;
    const picked = clusters ? await setFolderOf({ clusters }, deps) : null;
    if (picked) return { kind: "set-folder", folderName: picked.folderName };
    // 3. the root
    const r = await rootFolderFor(rec, {
      ...deps,
      sessions: () => Promise.resolve(all),
    });
    if (r.kind === "resolved")
      return { kind: "root", path: rootPath(r.rootName, r.path) };
    if (r.kind === "shared")
      return {
        kind: "shared",
        folderName: r.folderName,
        rootName: r.rootName,
        ...(r.count !== undefined ? { count: r.count } : {}),
      };
    return stored && isLooseDrop(stored)
      ? { kind: "ask", loose: true }
      : { kind: "ask" };
  } catch {
    return { kind: "ask" };
  }
}

// ── DI-3-S18-FE — the heal after a pick ───────────────────────────────────

/**
 * DI-3-S18-FE — a folder the pilot picked (or dropped) for a PAUSED session
 * (the board's "Choose folder", the card's folder pick, the Upload page):
 * when it holds EVERY `name|size` key of THIS record (the lock
 * no-wrong-session-made-active: the record's own keys, never another's), the
 * record heals — the folder's name, and where it sits under the granted root
 * (`root.resolve(handle)`, `rootPathOf`; no root / no answer → the old path is
 * dropped when the name changed). The picked folder may be a parent of the
 * drop folder: the read is recursive, the record then names the parent. A
 * folder missing any key, a loose-file record or a record without keys →
 * nothing written. Resolves whether the record was rewritten.
 */
export async function healFromFolder(
  rec: Pick<
    SessionRecord,
    "sessionId" | "folderName" | "clusters" | "rootPath" | "looseDrop"
  > &
    Partial<Pick<SessionRecord, "handleKey">>,
  result: Pick<CollectResult, "files" | "folderName">,
  handle: unknown,
  deps: {
    rename?: typeof renameSessionFolder;
    pathOf?: typeof rootPathOf;
  } = {},
): Promise<boolean> {
  const name = result.folderName;
  if (!name || isLooseDrop(rec)) return false;
  const keys = recordFileKeys(rec);
  if (keys.size === 0) return false;
  const held = new Set(
    result.files.map((f) =>
      rowHashKey({ relativePath: f.relativePath, size: f.file.size }),
    ),
  );
  if (![...keys].every((k) => held.has(k))) return false;
  const path = handle
    ? await (deps.pathOf ?? rootPathOf)(handle).catch(() => null)
    : null;
  const samePath =
    path === null || path.join("\n") === (rec.rootPath ?? []).join("\n");
  if (name === rec.folderName && samePath) return false;
  return (deps.rename ?? renameSessionFolder)(rec.sessionId, name, path).catch(
    () => false,
  );
}

// ── DI-3-S16-FE — "a folder for this set is readable now" ─────────────────

const readableListeners = new Set<(daId: string) => void>();

/**
 * A folder was loaded on a card for `daId` (a pick with found > 0, or the
 * remembered folder re-asked): every Resume line of that set reads its
 * sources again — the upload continues without a further click.
 */
export function announceSetFolder(daId: string): void {
  for (const l of readableListeners) l(daId);
}

export function onSetFolder(fn: (daId: string) => void): () => void {
  readableListeners.add(fn);
  return () => {
    readableListeners.delete(fn);
  };
}
